import * as React from 'react';
import { LogIn, Menu, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle, } from '@/components/ui/navigation-menu';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Logo } from '@/components/logo';
import { cn } from '@/lib/utils';
// shadcn's `navigationMenuTriggerStyle` bakes `bg-background` into every
// item, which paints solid rounded rectangles per-link on top of the
// translucent header. Override with transparent + a subtle hover/active
// fill so items blend into the header strip the way Stripe/Linear do.
const navTriggerOverride = '!bg-transparent hover:!bg-accent/40 data-[state=open]:!bg-accent/40';
export const SiteHeader = () => {
    const { t } = useTranslation();
    const { location: { pathname }, } = useRouterState();
    const isActive = (href) => pathname === href || pathname.startsWith(`${href}/`);
    return (<header className="sticky top-0 z-50 w-full border-b bg-background/70 backdrop-blur">
            <div className="mx-auto max-w-7xl px-4">
                <div className="flex h-14 items-center justify-between">
                    {/* Logo */}
                    <Logo to="/"/>

                    {/* DESKTOP NAV — product-first ordering. The two core
            product items lead (Smart Link, What is Adora), then
            product (Features), proof (Case studies), the
            conversion item (Pricing), and finally the dropdown
            resources (Learn, Compare). Pricing in the middle is
            a deliberate Stripe/Linear pattern — converts faster
            than burying it at the very end. */}
                    <NavigationMenu className="hidden md:flex">
                        <NavigationMenuList>
                            <NavLink label="Smart Link" href="/smart-link" active={isActive('/smart-link')}/>
                            <NavLink label="What is Adora" href="/what-is-adora" active={isActive('/what-is-adora')}/>
                            <NavLink label="Features" href="/features" active={isActive('/features')}/>
                            <NavLink label="Case studies" href="/case-studies" active={isActive('/case-studies')}/>
                            <NavLink label={t('nav_pricing')} href="/pricing" active={isActive('/pricing')}/>

                            {/* Learn dropdown */}
                            <NavigationMenuItem>
                                <NavigationMenuTrigger className={navTriggerOverride}>Learn</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <MenuPanel>
                                        <MenuItem label="Offline marketing analytics" href="/learn/offline-marketing-analytics"/>
                                        <MenuItem label="Smart Links explained" href="/learn/smart-links"/>
                                        <MenuItem label="QR scan tracking" href="/learn/qr-scan-tracking"/>
                                        <MenuItem label="One-link engine" href="/learn/one-link-engine"/>
                                    </MenuPanel>
                                </NavigationMenuContent>
                            </NavigationMenuItem>

                            {/* Compare dropdown */}
                            <NavigationMenuItem>
                                <NavigationMenuTrigger className={navTriggerOverride}>Compare</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <MenuPanel>
                                        <MenuItem label="Adora vs Linktree" href="/compare/linktree"/>
                                        <MenuItem label="Adora vs HeyLink" href="/compare/heylink"/>
                                        <MenuItem label="Adora vs Flowcode" href="/compare/flowcode"/>
                                    </MenuPanel>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>

                    {/* DESKTOP CTA */}
                    <div className="hidden md:flex items-center gap-2">
                        <Button variant="ghost" asChild>
                            <Link to="/sign-in" search={{ redirect: '/console' }}>
                                <LogIn className="mr-1 h-4 w-4"/>
                                {t('sign_in')}
                            </Link>
                        </Button>
                        <Button asChild>
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                <UserPlus className="mr-1 h-4 w-4"/>
                                {t('get_started')}
                            </Link>
                        </Button>
                    </div>

                    {/* MOBILE */}
                    <MobileMenu />
                </div>
            </div>
        </header>);
};
/* ---------- Helpers ---------- */
function NavLink({ label, href, active }) {
    return (<NavigationMenuItem>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle({
            className: cn(navTriggerOverride, active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'),
        })}>
                <Link to={href}>{label}</Link>
            </NavigationMenuLink>
        </NavigationMenuItem>);
}
function MenuPanel({ children }) {
    return <ul className="grid w-100 gap-2 md:w-125 md:grid-cols-2 lg:w-150">{children}</ul>;
}
function MenuItem({ label, href, ...props }) {
    return (<li {...props}>
            <NavigationMenuLink asChild>
                <Link to={href} className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                    {label}
                </Link>
            </NavigationMenuLink>
        </li>);
}
function MobileMenu() {
    const { t } = useTranslation();
    return (<div className="flex md:hidden items-center gap-2">
            <Button asChild size="sm">
                <Link to="/sign-up" search={{ redirect: '/console' }}>
                    {t('get_started')}
                </Link>
            </Button>

            <Sheet>
                <SheetTrigger asChild>
                    <Button size="icon" variant="outline">
                        <Menu className="h-5 w-5"/>
                    </Button>
                </SheetTrigger>

                <SheetContent side="right" className="w-[85vw] p-4">
                    <nav className="space-y-1">
                        <MobileLink to="/smart-link" label="Smart Link"/>
                        <MobileLink to="/what-is-adora" label="What is Adora"/>
                        <MobileLink to="/features" label="Features"/>
                        <MobileLink to="/case-studies" label="Case studies"/>
                        <MobileLink to="/pricing" label="Pricing"/>
                        <MobileLink to="/learn" label="Learn"/>
                        <MobileLink to="/compare" label="Compare"/>
                    </nav>

                    <div className="mt-4 border-t pt-4 space-y-2">
                        <Button variant="ghost" asChild className="w-full">
                            <Link to="/sign-in" search={{ redirect: '/console' }}>
                                {t('sign_in')}
                            </Link>
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </div>);
}
function MobileLink({ to, label }) {
    return (<SheetClose asChild>
            <Link to={to} className="block rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                {label}
            </Link>
        </SheetClose>);
}
